import { useMutation } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ROUTES } from "@/app/routes";
import { resetPassword } from "@/features/identity/identity.api";
import { PASSWORD_MIN_LENGTH } from "@/features/identity/schemas/auth.schema";
import { pendingConsentReturnTo } from "@/shared/auth/oauth-return-to";
import { LogoLockup } from "@/shared/components/brand/logo-mark";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";

const route = getRouteApi(ROUTES.auth.resetPassword.id);

/**
 * `/reset-password?token=...`. Landing for the emailed reset link. Verifies
 * nothing up front, the token is consumed when the new password is submitted.
 * A missing token short-circuits to a friendly "link invalid" panel.
 */
export function ResetPasswordRoute() {
  const { token } = route.useSearch();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const reset = useMutation({ mutationFn: resetPassword });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!token) return;
    if (password.length < PASSWORD_MIN_LENGTH) {
      setError(`Use a password of at least ${PASSWORD_MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    try {
      await reset.mutateAsync({ token, password });
      setDone(true);
    } catch {
      setError("This reset link is invalid or has expired. Request a new one.");
    }
  }

  if (!token) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 px-6 py-12 text-center">
        <LogoLockup className="w-fit" />
        <h1 className="font-display text-2xl font-semibold tracking-tight">
          This link is no longer valid
        </h1>
        <p className="text-sm text-text-secondary">
          Reset links expire after 15 minutes and can only be used once.
        </p>
        <Button asChild>
          <Link to="/forgot-password" search={{}}>
            Request a new link
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-8 px-6 py-12">
      <div>
        <LogoLockup className="w-fit" />
        <h1 className="font-display mt-4 text-3xl font-semibold tracking-tighter">
          Choose a new password
        </h1>
        <p className="mt-2 text-sm text-text-secondary">
          Pick a password you don't use anywhere else.
        </p>
      </div>

      {done ? (
        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-border bg-surface-subtle p-4 text-sm">
            <p className="font-medium">Password updated.</p>
            <p className="mt-1 text-text-secondary">You can now sign in with your new password.</p>
          </div>
          <Button
            onClick={() => {
              const consentReturn = pendingConsentReturnTo();
              if (consentReturn) window.location.assign(consentReturn);
              else void navigate({ to: "/login", search: {} });
            }}
          >
            Go to sign in
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="password">New password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="confirmPassword">Confirm new password</Label>
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <Button type="submit" disabled={reset.isPending}>
            {reset.isPending ? "Updating…" : "Update password"}
          </Button>
        </form>
      )}
    </div>
  );
}
